import { Box, Button, Dialog, DialogTitle, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

interface RenameDashboardDialogProps {
  open: boolean;
  /** The dashboard's current name; the field starts pre-filled with this. */
  initialName: string;
  saving: boolean;
  /** Set when the last save attempt failed; shown inline instead of closing the dialog. */
  error?: string | null;
  onCancel: () => void;
  onSave: (name: string) => void;
}

/**
 * Shared rename UI for a dashboard's name, used from both the "My dashboards"
 * list (its own "..." menu) and from inside a dashboard (its "..." menu), so
 * the two entry points stay on one implementation instead of drifting.
 */
const RenameDashboardDialog = ({ open, initialName, saving, error, onCancel, onSave }: RenameDashboardDialogProps) => {
  const [value, setValue] = useState(initialName);

  useEffect(() => {
    if (open) setValue(initialName);
  }, [open, initialName]);

  const trimmed = value.trim();

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth='xs'>
      <DialogTitle sx={{ pb: 0.5 }}>Rename dashboard</DialogTitle>
      <Box sx={{ p: 3, pt: 2 }}>
        <TextField
          label='Name'
          size='small'
          fullWidth
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && trimmed && !saving) onSave(trimmed);
          }}
        />
        {error && (
          <Typography variant='body2' color='error' sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, pb: 2 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          disableElevation
          disabled={!trimmed || saving}
          onClick={() => onSave(trimmed)}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default RenameDashboardDialog;
